"use client";

import NavCoach from "@/components/nav/NavCoach";
import PlaybookChatAnswer from "@/components/playbook/PlaybookChatAnswer";
import AgentActionLinks from "@/components/agent-actions/AgentActionLinks";
import { useChat } from "@/hooks/useChatAdapter";
import { useSabineChatPersistence } from "@/hooks/useSabineChatPersistence";

// Must match app/api/home/sabine/route.js, which logs every Q&A under
// buildAIChatFeedbackMessageId(FEEDBACK_SOURCE, <user message id>) — a vote
// in the chat then updates that row instead of creating a second one, and
// the chat itself writes nothing (`captureFeedback` off).
const FEEDBACK_SOURCE = "HOME_SABINE";
const FEEDBACK_CONTEXT_TYPE = "HOME_CHAT";

/**
 * Sabine on the home page: the "ask anything" chat, open beside the rail from
 * the moment the page loads. Streams from /api/home/sabine, grounded in the
 * playbook, recent practice, learning paths and due work. The thread persists
 * per user and studio, so switching studios never shows a thread about
 * another studio's team; "New chat" in the header clears it.
 *
 * @param {object} props
 * @param {string} props.userId
 * @param {string} props.workspaceId
 * @param {string[]} props.prompts     The suggested questions for this view
 * @param {string} props.placeholder  What the composer invites
 */
export default function HomeCoach({ userId, workspaceId, prompts, placeholder }) {
  const {
    messages,
    input,
    setInput,
    handleInputChange,
    isLoading,
    sendMessage,
    setMessages,
    error,
  } = useChat({ api: "/api/home/sabine", initialMessages: [] });

  const { resetAndClear } = useSabineChatPersistence({
    source: FEEDBACK_SOURCE,
    contextId: workspaceId,
    messages,
    setMessages,
    enabled: Boolean(userId && workspaceId),
  });

  // The composer also hands over `{ enableWebSearch }`; the home route has
  // no web search, so it is not read.
  const send = () => {
    const value = input.trim();
    if (!value || isLoading) return;
    setInput("");
    sendMessage({ role: "user", content: value }).catch((error) => {
      console.error("Failed to send Sabine message", error);
    });
  };

  return (
    <NavCoach
      defaultOpen
      placeholder={placeholder}
      messages={messages}
      isProcessing={isLoading}
      inputValue={input}
      onInputChange={handleInputChange}
      setInputValue={setInput}
      onSubmit={send}
      error={error?.message}
      prompts={prompts}
      onPromptSelect={setInput}
      onReset={() => void resetAndClear()}
      feedbackSource={FEEDBACK_SOURCE}
      feedbackContext={{ contextType: FEEDBACK_CONTEXT_TYPE, workspaceId }}
      captureFeedback={false}
      renderMessageContent={(message) => (
        <>
          <PlaybookChatAnswer
            content={message.content}
            resources={(message.parts || []).flatMap((part) =>
              part.type === "tool-get_playbook_resources" && part.state === "output-available"
                ? part.output?.items || []
                : [],
            )}
          />
          <AgentActionLinks content={message.content || ""} className="mt-3" />
        </>
      )}
    />
  );
}
